import { ProfileType, UserRole, type Permission } from "@/types";
import type { UserLegalInfo } from "@/services/profileService";
import { ADMIN_STATS_SECTION_PERMISSIONS } from "./hooks";

export type DashboardKind =
  "customer" | "admin" | "salesAgent" | "host" | "partner";

/** Which dashboard a user lands on: staff is recognised by the admin stats permissions, not by role. */
export function dashboardKindFor(
  role: UserRole | undefined,
  hasPermission: (permission: Permission) => boolean,
): DashboardKind {
  if (role === UserRole.CUSTOMER) {
    return "customer";
  }
  if (
    ADMIN_STATS_SECTION_PERMISSIONS.some((permission) =>
      hasPermission(permission),
    )
  ) {
    return "admin";
  }
  if (role === UserRole.SALES_AGENT) {
    return "salesAgent";
  }
  if (role === UserRole.HOST) {
    return "host";
  }
  return "partner";
}

type TimeGreetingKey =
  "greeting.morning" | "greeting.afternoon" | "greeting.evening";

/** `common` i18n key greeting the user at `hour` (0-23, local time). */
export function timeGreetingKey(hour: number): TimeGreetingKey {
  if (hour < 12) {
    return "greeting.morning";
  }
  if (hour < 18) {
    return "greeting.afternoon";
  }
  return "greeting.evening";
}

/** Name to greet: legal representative or company for a company, else first/last name. */
export function profileDisplayName(
  profile: UserLegalInfo | undefined,
): string | null {
  if (!profile) {
    return null;
  }
  if (profile.profile_type === ProfileType.COMPANY) {
    return profile.legal_rep_name.trim() || profile.company_name.trim() || null;
  }
  const firstName = profile.first_name.trim();
  const lastName = profile.last_name.trim();
  if (firstName && lastName) {
    return `${firstName} ${lastName}`;
  }
  return firstName || lastName || null;
}
